import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  LoadingEntity,
  Sort,
  ToasterService
} from '@inst-iot/bosch-angular-ui-components';
import { Vehicle } from 'src/app/_shared/models/vehicle';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { sortRbTableData } from 'src/app/_shared/utils/sort-utils';
import { NotificationToastComponent } from 'src/app/_shared/components/utils/notification-toast/notification-toast.component';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  providers: [
    {
      provide: ToasterService,
      useFactory: () => new ToasterService(NotificationToastComponent)
    }
  ]
})
export class SettingsComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];

  readonly bitcGroupName = 'bitc';

  loader = new LoadingEntity();
  dataLoading = false;
  errorMessage = false;

  vehicles: Vehicle[] = [];
  displayVehicles: Vehicle[] = [];
  searchQuery = '';
  sort: Sort = { id: '', direction: '' };

  // Group membership is seeded from `deviceGroup` and kept in sync via POST vehicles-group.
  inBitcGroup: { [vehicleId: string]: boolean } = {};
  selected: { [vehicleId: string]: boolean } = {};

  constructor(
    private readonly vehicleInfoService: VehicleInfoService,
    private readonly toaster: ToasterService
  ) {}

  ngOnInit(): void {
    this.loadVehicles();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // Arrow property: ngFor invokes trackBy without the component context.
  readonly trackByVehicleId = (_index: number, vehicle: Vehicle): string => this.getVehicleId(vehicle);

  getVehicleId(vehicle: Vehicle): string {
    return vehicle._id ?? vehicle.deviceId ?? vehicle.batteryIdentifier;
  }

  loadVehicles(): void {
    this.dataLoading = true;
    this.errorMessage = false;

    this.vehicleInfoService.getAllVehicles(this.fleetId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          this.vehicles = res.data || [];
          this.selected = {};
          this.vehicles.forEach(vehicle => {
            const id = this.getVehicleId(vehicle);
            this.inBitcGroup[id] = this.hasBitcGroup(vehicle);
          });
          this.applySearchAndSort();
          this.dataLoading = false;
        },
        error: err => {
          console.error('Error fetching vehicles:', err);
          this.vehicles = [];
          this.displayVehicles = [];
          this.errorMessage = true;
          this.dataLoading = false;
        }
      });
  }

  checkNewVehicles(): void {
    this.vehicleInfoService.storeVehicles({}).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        this.openToast(
          'Battery in the Cloud is currently retrieving and processing MyGeotab assets. The time required to complete this operation depends on the number of assets. In the meantime, you can continue exploring the dashboard.',
          'rb-ic-alert-success',
          'success'
        );
        this.loadVehicles();
      },
      error: error => {
        console.error('Error storing vehicles:', error);
        this.openToast('Could not retrieve new vehicles. Please try again.', 'rb-ic-alert-error', 'error');
      }
    });
  }

  onSortChange(sort: Sort): void {
    this.sort = sort;
    this.applySearchAndSort();
  }

  applySearchAndSort(): void {
    const query = this.searchQuery.trim().toLowerCase();
    const filtered = query
      ? this.vehicles.filter(vehicle =>
        [vehicle.name, vehicle.batteryIdentifier, vehicle.vin, vehicle.vehicleBrand, vehicle.vehicleModel]
          .some(value => (value ?? '').toString().toLowerCase().includes(query))
      )
      : this.vehicles.slice();

    this.displayVehicles = sortRbTableData(this.sort, filtered);
  }

  isSelected(vehicle: Vehicle): boolean {
    return !!this.selected[this.getVehicleId(vehicle)];
  }

  toggleSelection(vehicle: Vehicle, checked: boolean): void {
    if (this.isInGroup(vehicle) && checked) {
      return;
    }
    const id = this.getVehicleId(vehicle);
    if (checked) {
      this.selected[id] = true;
    } else {
      delete this.selected[id];
    }
  }

  get selectableVehicles(): Vehicle[] {
    return this.displayVehicles.filter(vehicle => !this.isInGroup(vehicle));
  }

  get selectedVehicles(): Vehicle[] {
    return this.displayVehicles.filter(vehicle => this.isSelected(vehicle));
  }

  get selectedCount(): number {
    return this.selectedVehicles.length;
  }

  get allSelected(): boolean {
    return this.selectableVehicles.length > 0 && this.selectableVehicles.every(vehicle => this.isSelected(vehicle));
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.selectableVehicles.forEach(vehicle => this.toggleSelection(vehicle, true));
    } else {
      this.displayVehicles.forEach(vehicle => this.toggleSelection(vehicle, false));
    }
  }

  get selectedNotInGroupCount(): number {
    return this.selectedVehicles.filter(vehicle => !this.isInGroup(vehicle)).length;
  }

  isInGroup(vehicle: Vehicle): boolean {
    return !!this.inBitcGroup[this.getVehicleId(vehicle)];
  }

  addToGroup(vehicles: Vehicle[]): void {
    const eligible = vehicles.filter(vehicle => !this.isInGroup(vehicle));
    this.updateGroupMembership(eligible, true, 'added to');
  }

  removeFromGroup(vehicles: Vehicle[]): void {
    const eligible = vehicles.filter(vehicle => this.isInGroup(vehicle));
    this.updateGroupMembership(eligible, false, 'removed from');
  }

  private updateGroupMembership(vehicles: Vehicle[], group: boolean, actionLabel: string): void {
    if (vehicles.length === 0) {
      return;
    }
    const body = vehicles.map(vehicle => ({
      deviceId: vehicle.deviceId,
      batteryIdentifiers: vehicle.batteryIdentifier,
      group
    }));
    this.vehicleInfoService.updateVehiclesGroup(body)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          vehicles.forEach(vehicle => {
            const id = this.getVehicleId(vehicle);
            this.inBitcGroup[id] = group;
            delete this.selected[id];
          });
          this.openToast(this.buildActionLabel(vehicles, actionLabel), 'checkmark', 'success');
        },
        error: error => {
          console.error('Error updating vehicles group:', error);
          this.openToast(
            `Could not update the "${this.bitcGroupName}" group for the selected vehicles. Please try again.`,
            'rb-ic-alert-error',
            'error'
          );
        }
      });
  }

  addSelectedToGroup(): void {
    this.addToGroup(this.selectedVehicles);
  }

  private buildActionLabel(vehicles: Vehicle[], action: string): string {
    const target = vehicles.length === 1
      ? `"${vehicles[0].name || this.getVehicleId(vehicles[0])}"`
      : `${vehicles.length} vehicles`;
    return `${target} ${action} the "${this.bitcGroupName}" group.`;
  }

  private hasBitcGroup(vehicle: Vehicle): boolean {
    return vehicle.group === true;
  }

  private openToast(label: string, icon: string, type: 'error' | 'success' | 'neutral'): void {
    this.toaster.createToast(label, icon, {
      timeout: 8000,
      customProperties: { margin: '0px', type }
    });
  }
}
